import {
  getAccessToken,
  hasAuthSession,
  isTokenExpired,
  normalizeAccessToken,
} from '@/utils/auth-token';
import { redirectUnauthenticatedToHome } from '@/utils/auth-redirect';
import { getAuthGeneration, signOutLocalSession } from '@/utils/auth-teardown';
import { createAccountDisabledSessionHandler } from '@/utils/account-disabled-session';
import { trackAnalyticsLog } from '@/utils/analytics-track';

let openLoginDialogFn = null;
let pendingLoginRedirect = null;
let pendingAuthPrompt = false;
/** @type {null | { hideWalletOptions?: boolean, contextKey?: string, redirectAfterLogin?: string, source?: string }} */
let pendingAuthDialogOptions = null;
const stateListeners = new Set();
/** 避免 validateAuthSession 重复广播相同登录态（如每次点收藏都 reload watchlist） */
let lastNotifiedAuthLoggedIn = null;

const LOGIN_RETURN_STORAGE_KEY = 'auth-login-return-path';

export function captureAuthRequestSession(token = getAccessToken()) {
  const normalized = normalizeAccessToken(token);
  return {
    token: normalized,
    generation: getAuthGeneration(),
    owned: Boolean(normalized && normalized === normalizeAccessToken(getAccessToken())),
  };
}

const accountDisabledSession = createAccountDisabledSessionHandler({
  getSession: captureAuthRequestSession,
  invalidateSession: () => invalidateAuthSession({ redirectHome: true }),
});

export const handleAccountDisabledSession = accountDisabledSession.handle;
export const subscribeAccountDisabledNotice = accountDisabledSession.subscribe;
export const dismissAccountDisabledNotice = accountDisabledSession.dismiss;

function normalizeRedirectPath(path) {
  const raw = String(path ?? '').trim();
  if (!raw || raw.startsWith('/auth/')) return '';
  return raw;
}

/** 当前页面路径（用于登录后回跳），排除 OAuth 回调页 */
export function getCurrentLoginReturnPath() {
  if (typeof window === 'undefined') return '';
  return normalizeRedirectPath(
    `${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

export function setOpenLoginDialog(handler) {
  openLoginDialogFn = typeof handler === 'function' ? handler : null;
  if (pendingAuthPrompt && openLoginDialogFn) {
    pendingAuthPrompt = false;
    openLoginDialogFn();
  }
}

export function setPendingLoginRedirect(path) {
  pendingLoginRedirect = normalizeRedirectPath(path) || null;
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (pendingLoginRedirect) {
      sessionStorage.setItem(LOGIN_RETURN_STORAGE_KEY, pendingLoginRedirect);
    } else {
      sessionStorage.removeItem(LOGIN_RETURN_STORAGE_KEY);
    }
  } catch {
    /* ignore quota */
  }
}

/** 登出：清内存 + session 回跳，避免下一账号沿用旧路径 */
export function clearPendingLoginRedirect() {
  pendingLoginRedirect = null;
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(LOGIN_RETURN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** 仅当尚无 pending redirect 时写入（保留显式 intent，如 /create-market） */
export function ensurePendingLoginRedirect(path) {
  if (pendingLoginRedirect) return;
  const normalized = normalizeRedirectPath(path);
  if (normalized) {
    setPendingLoginRedirect(normalized);
  }
}

/** Google OAuth 整页跳转前：将回跳路径写入 sessionStorage */
export function persistPendingLoginRedirectForOAuth() {
  if (typeof sessionStorage === 'undefined') return;
  const path = pendingLoginRedirect || getCurrentLoginReturnPath();
  if (path) {
    sessionStorage.setItem(LOGIN_RETURN_STORAGE_KEY, path);
  }
}

export function consumePendingLoginRedirect() {
  let path = pendingLoginRedirect;
  pendingLoginRedirect = null;
  if (!path && typeof sessionStorage !== 'undefined') {
    path = sessionStorage.getItem(LOGIN_RETURN_STORAGE_KEY);
    sessionStorage.removeItem(LOGIN_RETURN_STORAGE_KEY);
  }
  return normalizeRedirectPath(path) || null;
}

export function subscribeAuthState(listener) {
  stateListeners.add(listener);
  return () => stateListeners.delete(listener);
}

function notifyAuthState(loggedIn) {
  if (lastNotifiedAuthLoggedIn === loggedIn) return;
  lastNotifiedAuthLoggedIn = loggedIn;
  if (loggedIn) dismissAccountDisabledNotice();
  stateListeners.forEach((listener) => {
    try {
      listener(loggedIn);
    } catch (err) {
      console.warn('[auth] state listener failed', err);
    }
  });
}

export function syncAuthStateValue() {
  return hasAuthSession();
}

export function validateAuthSession() {
  if (getAccessToken() && isTokenExpired()) {
    signOutLocalSession();
    notifyAuthState(false);
    redirectIfAuthenticatedRoute();
    return false;
  }
  const loggedIn = hasAuthSession();
  notifyAuthState(loggedIn);
  return loggedIn;
}

function redirectIfAuthenticatedRoute() {
  redirectUnauthenticatedToHome();
}

export function invalidateAuthSession({ redirectHome = true } = {}) {
  signOutLocalSession();
  notifyAuthState(false);
  if (redirectHome) {
    redirectIfAuthenticatedRoute();
  }
}

/** 被动未登录：弹登录框并记录回跳路径，不离开当前公开页 */
export function promptAuthLogin(redirectAfterLogin) {
  if (validateAuthSession()) return true;
  // 每次触发都刷新回跳，避免沿用更早打开登录时留下的 `/`
  const redirect = redirectAfterLogin || getCurrentLoginReturnPath();
  setPendingLoginRedirect(redirect);
  const redirectPath = String(redirect ?? '').trim();
  trackAnalyticsLog({
    key: 'auth.prompt',
    label: '被动唤起登录',
    value: { redirect: redirectPath ? redirectPath.slice(0, 120) : undefined },
  });
  openAuthDialog({ source: 'prompt', redirectAfterLogin: redirectPath || undefined });
  return false;
}

export function consumeAuthDialogOptions() {
  const opts = pendingAuthDialogOptions;
  pendingAuthDialogOptions = null;
  return opts ?? {};
}

/**
 * 打开登录弹窗（不校验是否已登录，供 OAuth 回调续接授权等场景）
 * @param {{ hideWalletOptions?: boolean, contextKey?: string, redirectAfterLogin?: string, source?: string }} [options]
 */
export function openAuthDialog(options = null) {
  pendingAuthDialogOptions = options && typeof options === 'object' ? { ...options } : null;
  if (openLoginDialogFn) {
    openLoginDialogFn();
    return;
  }
  pendingAuthPrompt = true;
}

export function performLocalLogout() {
  clearPendingLoginRedirect();
  try {
    // 动态 import 会慢一拍；同步清 key 更稳
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('vantag-privy-pending-auth');
      sessionStorage.removeItem('ds-firebase-google-redirect');
    }
  } catch {
    /* ignore */
  }
  signOutLocalSession();
  notifyAuthState(false);
}

export function initAuthSessionState() {
  if (getAccessToken() && isTokenExpired()) {
    signOutLocalSession();
    return false;
  }
  return hasAuthSession();
}
